import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronRight, ChevronLeft, User, Briefcase, MapPin, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { registerDoctor, locationsAPI, specialitiesAPI } from '../lib/api';
import { toast } from 'sonner';

export const DoctorRegistrationPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [specialities, setSpecialities] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience_years: '',
    specialities: [],
    clinic_name: '',
    clinic_address: '',
    location_id: '',
    bio: '',
    photo_url: '',
    certifications: '',
    agree_terms: false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locRes, specRes] = await Promise.all([
          locationsAPI.getAll(),
          specialitiesAPI.getAll(),
        ]);
        setLocations(locRes.data);
        setSpecialities(specRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const steps = [
    { number: 1, title: 'Personal Info', icon: User },
    { number: 2, title: 'Professional', icon: Briefcase },
    { number: 3, title: 'Location', icon: MapPin },
    { number: 4, title: 'Additional', icon: FileText },
  ];

  const validateStep = (stepNumber) => {
    const newErrors = {};
    
    if (stepNumber === 1) {
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    }
    
    if (stepNumber === 2) {
      if (!formData.experience_years) newErrors.experience_years = 'Experience is required';
      if (formData.specialities.length === 0) newErrors.specialities = 'Select at least one speciality';
    }
    
    if (stepNumber === 3) {
      if (!formData.clinic_name.trim()) newErrors.clinic_name = 'Clinic name is required';
      if (!formData.clinic_address.trim()) newErrors.clinic_address = 'Clinic address is required';
      if (!formData.location_id) newErrors.location_id = 'Select a city';
    }
    
    if (stepNumber === 4) {
      if (!formData.agree_terms) newErrors.agree_terms = 'You must agree to the terms';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSpecialityToggle = (specId) => {
    setFormData(prev => ({
      ...prev,
      specialities: prev.specialities.includes(specId)
        ? prev.specialities.filter(id => id !== specId)
        : [...prev.specialities, specId]
    }));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        experience_years: parseInt(formData.experience_years),
        certifications: formData.certifications ? formData.certifications.split(',').map(c => c.trim()) : [],
      };
      delete submitData.agree_terms;

      await registerDoctor(submitData);
      toast.success('Registration submitted successfully!');
      navigate('/registration-success');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-alabaster py-12 px-4" data-testid="doctor-registration-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-navy-900 mb-4">
            Join DrPortals
          </h1>
          <p className="text-gray-600">
            Register as a verified dermatologist and connect with patients
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-12 relative">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 -z-10" />
          {steps.map((s) => {
            const Icon = s.icon;
            const isActive = step === s.number;
            const isComplete = step > s.number;
            
            return (
              <div key={s.number} className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    isComplete
                      ? 'bg-green-500 text-white'
                      : isActive
                      ? 'bg-navy-800 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {isComplete ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={`text-xs mt-2 ${isActive ? 'text-navy-800 font-medium' : 'text-gray-500'}`}>
                  {s.title}
                </span>
              </div>
            );
          })}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
          {/* Step 1: Personal Info */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1">
              <h2 className="font-heading text-xl font-semibold text-navy-900 mb-6">
                Personal Information
              </h2>
              
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  placeholder="Dr. John Doe"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className={errors.name ? 'border-red-500' : ''}
                  data-testid="input-name"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="doctor@example.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={errors.email ? 'border-red-500' : ''}
                  data-testid="input-email"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  placeholder="+91 98765 43210"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className={errors.phone ? 'border-red-500' : ''}
                  data-testid="input-phone"
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>
            </div>
          )}

          {/* Step 2: Professional */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-2">
              <h2 className="font-heading text-xl font-semibold text-navy-900 mb-6">
                Professional Details
              </h2>
              
              <div>
                <Label htmlFor="experience">Years of Experience *</Label>
                <Select value={formData.experience_years} onValueChange={(val) => handleChange('experience_years', val)}>
                  <SelectTrigger className={errors.experience_years ? 'border-red-500' : ''} data-testid="select-experience">
                    <SelectValue placeholder="Select experience" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 5, 7, 10, 15, 20, 25, 30].map(year => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}+ years
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.experience_years && <p className="text-red-500 text-sm mt-1">{errors.experience_years}</p>}
              </div>

              <div>
                <Label>Specialities * (Select all that apply)</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {specialities.map((spec) => (
                    <div
                      key={spec.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.specialities.includes(spec.id)
                          ? 'border-navy-800 bg-navy-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleSpecialityToggle(spec.id)}
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox checked={formData.specialities.includes(spec.id)} />
                        <span className="text-sm font-medium">{spec.name}</span>
                      </div>
                      <span className="text-xs text-gray-500 ml-6">{spec.category}</span>
                    </div>
                  ))}
                </div>
                {errors.specialities && <p className="text-red-500 text-sm mt-1">{errors.specialities}</p>}
              </div>
            </div>
          )}

          {/* Step 3: Location */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-3">
              <h2 className="font-heading text-xl font-semibold text-navy-900 mb-6">
                Clinic Location
              </h2>
              
              <div>
                <Label htmlFor="clinic_name">Clinic Name *</Label>
                <Input
                  id="clinic_name"
                  placeholder="Your Clinic Name"
                  value={formData.clinic_name}
                  onChange={(e) => handleChange('clinic_name', e.target.value)}
                  className={errors.clinic_name ? 'border-red-500' : ''}
                  data-testid="input-clinic-name"
                />
                {errors.clinic_name && <p className="text-red-500 text-sm mt-1">{errors.clinic_name}</p>}
              </div>

              <div>
                <Label htmlFor="location_id">City *</Label>
                <Select value={formData.location_id} onValueChange={(val) => handleChange('location_id', val)}>
                  <SelectTrigger className={errors.location_id ? 'border-red-500' : ''} data-testid="select-city">
                    <SelectValue placeholder="Select your city" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.city}, {loc.country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.location_id && <p className="text-red-500 text-sm mt-1">{errors.location_id}</p>}
              </div>

              <div>
                <Label htmlFor="clinic_address">Clinic Address *</Label>
                <Textarea
                  id="clinic_address"
                  placeholder="Full clinic address"
                  value={formData.clinic_address}
                  onChange={(e) => handleChange('clinic_address', e.target.value)}
                  className={errors.clinic_address ? 'border-red-500' : ''}
                  data-testid="input-clinic-address"
                />
                {errors.clinic_address && <p className="text-red-500 text-sm mt-1">{errors.clinic_address}</p>}
              </div>
            </div>
          )}

          {/* Step 4: Additional */}
          {step === 4 && (
            <div className="space-y-6" data-testid="step-4">
              <h2 className="font-heading text-xl font-semibold text-navy-900 mb-6">
                Additional Information
              </h2>
              
              <div>
                <Label htmlFor="bio">Bio (Optional)</Label>
                <Textarea
                  id="bio"
                  placeholder="Tell us about yourself and your practice..."
                  value={formData.bio}
                  onChange={(e) => handleChange('bio', e.target.value)}
                  rows={4}
                  data-testid="input-bio"
                />
              </div>

              <div>
                <Label htmlFor="photo_url">Profile Photo URL (Optional)</Label>
                <Input
                  id="photo_url"
                  placeholder="https://example.com/your-photo.jpg"
                  value={formData.photo_url}
                  onChange={(e) => handleChange('photo_url', e.target.value)}
                  data-testid="input-photo-url"
                />
              </div>

              <div>
                <Label htmlFor="certifications">Certifications (Optional)</Label>
                <Input
                  id="certifications"
                  placeholder="MD Dermatology, MBBS (comma separated)"
                  value={formData.certifications}
                  onChange={(e) => handleChange('certifications', e.target.value)}
                  data-testid="input-certifications"
                />
                <p className="text-xs text-gray-500 mt-1">Separate multiple certifications with commas</p>
              </div>

              <div className="flex items-start gap-2 pt-4">
                <Checkbox
                  id="agree_terms"
                  checked={formData.agree_terms}
                  onCheckedChange={(checked) => handleChange('agree_terms', checked)}
                  data-testid="checkbox-terms"
                />
                <Label htmlFor="agree_terms" className="text-sm text-gray-600 cursor-pointer">
                  I agree to the Terms of Service and Privacy Policy. I confirm that the information provided is accurate.
                </Label>
              </div>
              {errors.agree_terms && <p className="text-red-500 text-sm">{errors.agree_terms}</p>}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
            {step > 1 ? (
              <Button variant="outline" onClick={handleBack} className="gap-2">
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < 4 ? (
              <Button onClick={handleNext} className="bg-navy-800 hover:bg-navy-900 gap-2" data-testid="btn-next">
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-navy-800 hover:bg-navy-900"
                data-testid="btn-submit"
              >
                {loading ? 'Submitting...' : 'Submit Registration'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
